import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPortfolioAssessments, getPortfolioProfessionalSportsSummary, getPortfolioAssessmentsList } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  ClipboardCheck,
  FileText,
  TrendingUp,
  TrendingDown,
  Minus,
  Search,
  AlertTriangle,
  CheckCircle,
  Target,
  BarChart3,
  PieChart,
  Building2,
  ChevronRight,
  Loader2,
  Calendar,
  Eye,
  Globe,
  Medal,
  Shield,
  Zap,
  Activity,
  Dumbbell,
  Users,
} from 'lucide-react';
import { cn } from '../lib/utils';
import {
  PieChart as RechartsPie,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

const RISK_COLORS = {
  low: '#86efac',      // light green
  medium: '#fde047',   // light yellow
  high: '#fca5a5',     // light red
  critical: '#f87171', // slightly darker red for critical
};

const DirectionIcon = ({ direction, className }) => {
  const Icon = direction === 'increasing' ? TrendingUp : direction === 'decreasing' ? TrendingDown : Minus;
  const colorClass = direction === 'increasing' ? 'text-green-500' : direction === 'decreasing' ? 'text-red-500' : 'text-gray-500';
  return <Icon className={cn("w-4 h-4", colorClass, className)} />;
};

export default function PortfolioAssessments({ onDrillDown }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [proSportsData, setProSportsData] = useState(null);
  const [period, setPeriod] = useState('30');
  const [searchTerm, setSearchTerm] = useState('');
  const [riskFilter, setRiskFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('general');
  
  // State for assessments list
  const [assessmentsList, setAssessmentsList] = useState([]);
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [assessmentsTotal, setAssessmentsTotal] = useState(0);
  const [orgTypeFilter, setOrgTypeFilter] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [generalRes, proSportsRes] = await Promise.all([
        getPortfolioAssessments({ days: parseInt(period) }),
        getPortfolioProfessionalSportsSummary().catch(() => ({ data: null }))
      ]);
      setData(generalRes.data);
      setProSportsData(proSportsRes.data);
    } catch (error) {
      console.error('Error fetching portfolio assessments:', error);
    } finally {
      setLoading(false);
    }
  }, [period]);
  
  // Fetch individual assessments list
  const fetchAssessmentsList = useCallback(async () => {
    setAssessmentsLoading(true);
    try {
      const params = { days: parseInt(period), limit: 50 };
      if (orgTypeFilter !== 'all') {
        params.org_type = orgTypeFilter;
      }
      if (riskFilter !== 'all') {
        params.risk_level = riskFilter;
      }
      const res = await getPortfolioAssessmentsList(params);
      setAssessmentsList(res.data.assessments || []);
      setAssessmentsTotal(res.data.total || 0);
    } catch (error) {
      console.error('Error fetching assessments list:', error);
    } finally {
      setAssessmentsLoading(false);
    }
  }, [period, orgTypeFilter, riskFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Fetch assessments list when tab is general and filters change
  useEffect(() => {
    if (activeTab === 'general') {
      fetchAssessmentsList();
    }
  }, [activeTab, fetchAssessmentsList]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const riskTierData = data ? [
    { name: 'Low', value: data.risk_tiers.low, color: RISK_COLORS.low },
    { name: 'Medium', value: data.risk_tiers.medium, color: RISK_COLORS.medium },
    { name: 'High', value: data.risk_tiers.high, color: RISK_COLORS.high },
    { name: 'Critical', value: data.risk_tiers.critical, color: RISK_COLORS.critical },
  ].filter(d => d.value > 0) : [];

  const scoreDistData = data?.score_distribution ? Object.entries(data.score_distribution).map(([range, count]) => ({
    range,
    count,
  })) : [];

  const getRiskBandColor = (band) => {
    const colors = {
      'Elite': 'bg-emerald-500 text-white',
      'Strong': 'bg-green-500 text-white',
      'Moderate': 'bg-yellow-500 text-white',
      'Elevated': 'bg-orange-500 text-white',
      'Critical': 'bg-red-500 text-white',
    };
    return colors[band] || 'bg-gray-500 text-white';
  };
  
  const getRiskLevelColor = (level) => {
    const colors = {
      'low': 'bg-green-500/20 text-green-600 border-green-500/30',
      'medium': 'bg-yellow-500/20 text-yellow-600 border-yellow-500/30',
      'high': 'bg-orange-500/20 text-orange-600 border-orange-500/30',
      'critical': 'bg-red-500/20 text-red-600 border-red-500/30',
    };
    return colors[level] || 'bg-gray-500/20 text-gray-600 border-gray-500/30';
  };
  
  const getOrgTypeIcon = (orgType) => {
    if (orgType === 'fitness') return <Dumbbell className="w-4 h-4 text-green-500" />;
    return <Users className="w-4 h-4 text-blue-500" />;
  };
  
  // Filter assessments by search term
  const filteredAssessments = assessmentsList.filter(a => 
    !searchTerm || a.organization_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
            <Globe className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Portfolio Assessment Analytics</h2>
            <p className="text-sm text-muted-foreground">Aggregate risk assessment data across all organizations</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[150px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 Days</SelectItem>
              <SelectItem value="30">Last 30 Days</SelectItem>
              <SelectItem value="90">Last 90 Days</SelectItem>
              <SelectItem value="365">Last Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Assessment Type Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="general" className="flex items-center gap-2" data-testid="general-assessments-tab">
            <ClipboardCheck className="w-4 h-4" />
            <span>Sports & Fitness</span>
            <Badge variant="secondary" className="ml-1 text-xs">
              {(data?.by_org_type?.sports?.count || 0) + (data?.by_org_type?.fitness?.count || 0)}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="professional_sports" className="flex items-center gap-2" data-testid="professional-sports-tab">
            <Medal className="w-4 h-4" />
            <span>Professional Sports</span>
            {(data?.by_org_type?.professional_sports?.count || proSportsData?.assessed_orgs) > 0 && (
              <Badge variant="secondary" className="ml-1 text-xs bg-purple-500/20 text-purple-400">
                {data?.by_org_type?.professional_sports?.count || proSportsData?.assessed_orgs || 0}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* General Assessments Tab */}
        <TabsContent value="general" className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="metric-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Completion Rate</p>
                    <p className="text-3xl font-bold font-['Barlow_Condensed'] mt-1">
                      {data?.summary?.completion_rate || 0}%
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-blue-500" />
                  </div>
                </div>
                <Progress value={data?.summary?.completion_rate || 0} className="mt-3" />
              </CardContent>
            </Card>

            <Card className="metric-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Average Score</p>
                    <p className="text-3xl font-bold font-['Barlow_Condensed'] mt-1">
                      {data?.summary?.average_score || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Target className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Median: {data?.summary?.median_score || 0}
                </p>
              </CardContent>
            </Card>

            <Card className="metric-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Assessments</p>
                    <p className="text-3xl font-bold font-['Barlow_Condensed'] mt-1">
                      {data?.summary?.total_assessments || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                    <ClipboardCheck className="w-6 h-6 text-green-500" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {data?.summary?.unique_orgs_assessed || 0} organizations assessed
                </p>
              </CardContent>
            </Card>

            <Card className="metric-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Elevated Risk</p>
                    <p className="text-3xl font-bold font-['Barlow_Condensed'] mt-1 text-red-500">
                      {((data?.risk_tier_percentages?.high || 0) + (data?.risk_tier_percentages?.critical || 0)).toFixed(0)}%
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-red-500" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">High + Critical tiers</p>
              </CardContent>
            </Card>
          </div>

          {/* Charts and Analysis */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Risk Tier Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChart className="w-5 h-5" />
                  Risk Tier Distribution
                </CardTitle>
                <CardDescription>Assessment results by risk level</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie
                        data={riskTierData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {riskTierData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Score Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Score Distribution
                </CardTitle>
                <CardDescription>Assessment scores grouped by range</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={scoreDistData}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                      <XAxis dataKey="range" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#93c5fd" radius={[4, 4, 0, 0]} name="Assessments" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top Systemic Gaps */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                Top Systemic Gaps Across Portfolio
              </CardTitle>
              <CardDescription>
                Most common policy/procedure gaps identified across all organizations
              </CardDescription>
            </CardHeader>
            <CardContent>
              {data?.top_systemic_gaps?.length > 0 ? (
                <div className="space-y-3">
                  {data.top_systemic_gaps.map((gap, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center text-xs font-bold text-yellow-600">
                          {idx + 1}
                        </span>
                        <span className="text-sm">{gap.gap}</span>
                      </div>
                      <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/30">
                        {gap.frequency} organizations
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No systemic gaps identified
                </p>
              )}
            </CardContent>
          </Card>

          {/* Organization Type Breakdown */}
          {data?.by_org_type && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Assessment by Organization Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Sports Organizations</span>
                      <Badge variant="outline">{data.by_org_type.sports?.count || 0} assessments</Badge>
                    </div>
                    <p className="text-2xl font-bold mt-2">
                      {data.by_org_type.sports?.avg_score || 0} <span className="text-sm font-normal text-muted-foreground">avg score</span>
                    </p>
                  </div>
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Fitness Organizations</span>
                      <Badge variant="outline">{data.by_org_type.fitness?.count || 0} assessments</Badge>
                    </div>
                    <p className="text-2xl font-bold mt-2">
                      {data.by_org_type.fitness?.avg_score || 0} <span className="text-sm font-normal text-muted-foreground">avg score</span>
                    </p>
                  </div>
                  <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Professional Sports</span>
                      <Badge variant="outline" className="bg-purple-500/20">{data.by_org_type.professional_sports?.count || 0} assessments</Badge>
                    </div>
                    <p className="text-2xl font-bold mt-2">
                      {data.by_org_type.professional_sports?.avg_score || 0} <span className="text-sm font-normal text-muted-foreground">avg score</span>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Clickable Individual Assessments Table */}
          <Card>
            <CardHeader className="pb-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ClipboardCheck className="w-5 h-5" />
                    Individual Assessments
                  </CardTitle>
                  <CardDescription>
                    Click on any assessment to view details ({assessmentsTotal} total)
                  </CardDescription>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Select value={orgTypeFilter} onValueChange={setOrgTypeFilter}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Org Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="sports">Sports</SelectItem>
                      <SelectItem value="fitness">Fitness</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={riskFilter} onValueChange={setRiskFilter}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Risk Level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Levels</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {assessmentsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              ) : filteredAssessments.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <ClipboardCheck className="w-12 h-12 mx-auto mb-4 opacity-20" />
                  <p>No assessments found matching your filters</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Organization</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Score</TableHead>
                        <TableHead>Risk Level</TableHead>
                        <TableHead>Recommendations</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAssessments.map((assessment) => (
                        <TableRow 
                          key={assessment.id} 
                          className="cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => navigate(`/organizations/${assessment.organization_id}`)}
                          data-testid={`assessment-row-${assessment.id}`}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getOrgTypeIcon(assessment.org_type)}
                              <span className="font-medium">{assessment.organization_name}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {assessment.org_type?.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-lg">{Math.round(assessment.score)}%</span>
                              <Progress 
                                value={assessment.score} 
                                className="w-16 h-2" 
                              />
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={`capitalize ${getRiskLevelColor(assessment.risk_level)}`}>
                              {assessment.risk_level}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">
                              {assessment.recommendations_count || 0} items
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {assessment.created_at ? new Date(assessment.created_at).toLocaleDateString() : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/organizations/${assessment.organization_id}`);
                              }}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Professional Sports Tab */}
        <TabsContent value="professional_sports" className="space-y-6" data-testid="professional-sports-content">
          {proSportsData && proSportsData.total_professional_sports_orgs > 0 ? (
            <>
              {/* Critical Flags Alert */}
              {proSportsData.critical_flags_summary?.total_flags > 0 && (
                <Alert className="bg-red-500/10 border-red-500/30">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  <AlertDescription>
                    <div className="flex flex-col gap-2">
                      <span className="font-bold text-red-500 uppercase tracking-wide text-sm">
                        Critical Operational Flags Detected
                      </span>
                      <span className="text-red-400">
                        {proSportsData.critical_flags_summary?.orgs_with_flags || 0} organization(s) have {proSportsData.critical_flags_summary?.total_flags} critical control gaps requiring immediate attention.
                      </span>
                      {proSportsData.critical_flags_summary?.by_flag_type && Object.keys(proSportsData.critical_flags_summary.by_flag_type).length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-1">
                          {Object.entries(proSportsData.critical_flags_summary.by_flag_type).map(([flagName, flagData], idx) => (
                            <Badge key={idx} className="bg-red-500 text-white">
                              {flagName}: {flagData.count}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="metric-card bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Coverage</p>
                        <p className="text-3xl font-bold font-['Barlow_Condensed'] mt-1 text-green-500">
                          {proSportsData.assessment_coverage?.toFixed(0) || 0}%
                        </p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center">
                        <Shield className="w-6 h-6 text-purple-500" />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {proSportsData.assessed_orgs || 0}/{proSportsData.total_professional_sports_orgs || 0} assessed
                    </p>
                  </CardContent>
                </Card>

                <Card className="metric-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Avg Score</p>
                        <p className="text-3xl font-bold font-['Barlow_Condensed'] mt-1">
                          {proSportsData.average_enterprise_score?.toFixed(1) || '--'}
                        </p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                        <Target className="w-6 h-6 text-blue-500" />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">enterprise score</p>
                  </CardContent>
                </Card>

                <Card className="metric-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Risk Bands</p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {proSportsData.risk_band_distribution && Object.entries(proSportsData.risk_band_distribution).map(([band, count]) => (
                            count > 0 && (
                              <Badge key={band} className={`text-xs ${getRiskBandColor(band)}`}>
                                {count} {band}
                              </Badge>
                            )
                          ))}
                        </div>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-orange-500/10 flex items-center justify-center">
                        <Activity className="w-6 h-6 text-orange-500" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="metric-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Critical Flags</p>
                        <p className={`text-3xl font-bold font-['Barlow_Condensed'] mt-1 ${proSportsData.critical_flags_summary?.total_flags > 0 ? 'text-red-500' : 'text-green-500'}`}>
                          {proSportsData.critical_flags_summary?.total_flags || 0}
                        </p>
                      </div>
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${proSportsData.critical_flags_summary?.total_flags > 0 ? 'bg-red-500/10' : 'bg-green-500/10'}`}>
                        <Zap className={`w-6 h-6 ${proSportsData.critical_flags_summary?.total_flags > 0 ? 'text-red-500' : 'text-green-500'}`} />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {proSportsData.critical_flags_summary?.orgs_with_flags || 0} orgs affected
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Average Pillar Scores */}
              {proSportsData.average_pillar_scores && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-purple-500" />
                      Average Pillar Scores
                    </CardTitle>
                    <CardDescription>Enterprise risk assessment across three pillars</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Building2 className="w-4 h-4 text-blue-500" />
                          <span className="text-sm font-medium">Facilities & Infrastructure</span>
                        </div>
                        <Progress 
                          value={proSportsData.average_pillar_scores.facilities_infrastructure || 0} 
                          className="h-2 mb-1" 
                        />
                        <span className="text-lg font-bold">
                          {proSportsData.average_pillar_scores.facilities_infrastructure?.toFixed(1) || 0}%
                        </span>
                      </div>
                      <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Zap className="w-4 h-4 text-orange-500" />
                          <span className="text-sm font-medium">Event & Game Day Operations</span>
                        </div>
                        <Progress 
                          value={proSportsData.average_pillar_scores.event_operations || 0} 
                          className="h-2 mb-1" 
                        />
                        <span className="text-lg font-bold">
                          {proSportsData.average_pillar_scores.event_operations?.toFixed(1) || 0}%
                        </span>
                      </div>
                      <div className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Shield className="w-4 h-4 text-purple-500" />
                          <span className="text-sm font-medium">Cyber & Data Protection</span>
                        </div>
                        <Progress 
                          value={proSportsData.average_pillar_scores.cyber_data || 0} 
                          className="h-2 mb-1" 
                        />
                        <span className="text-lg font-bold">
                          {proSportsData.average_pillar_scores.cyber_data?.toFixed(1) || 0}%
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Organizations Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Medal className="w-5 h-5 text-purple-500" />
                    Professional Sports Organizations
                  </CardTitle>
                  <CardDescription>
                    {proSportsData.total_professional_sports_orgs || 0} total organizations
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {proSportsData.organizations && proSportsData.organizations.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Organization</TableHead>
                            <TableHead className="text-center">Score</TableHead>
                            <TableHead className="text-center">Risk Band</TableHead>
                            <TableHead className="text-center">Flags</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {proSportsData.organizations.map((org) => (
                            <TableRow key={org.organization_id} className="hover:bg-purple-500/5">
                              <TableCell>
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                                    <Medal className="w-4 h-4 text-purple-500" />
                                  </div>
                                  <div>
                                    <p className="font-medium">{org.organization_name}</p>
                                    <p className="text-xs text-muted-foreground">{org.city}, {org.state}</p>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                <span className="text-lg font-bold text-purple-500">
                                  {org.enterprise_score?.toFixed(1) || '--'}
                                </span>
                              </TableCell>
                              <TableCell className="text-center">
                                {org.risk_band ? (
                                  <Badge className={getRiskBandColor(org.risk_band)}>
                                    {org.risk_band}
                                  </Badge>
                                ) : (
                                  <Badge variant="outline">Not Assessed</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                {org.critical_flag_count > 0 ? (
                                  <Badge className="bg-red-500 text-white">
                                    <AlertTriangle className="w-3 h-3 mr-1" />
                                    {org.critical_flag_count}
                                  </Badge>
                                ) : org.enterprise_score ? (
                                  <Badge variant="outline" className="text-green-500 border-green-500/30">
                                    None
                                  </Badge>
                                ) : (
                                  <span className="text-muted-foreground">--</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => navigate(`/assessments/professional-sports/${org.organization_id}`)}
                                  className="text-purple-500 hover:text-purple-400 hover:bg-purple-500/10"
                                >
                                  {org.enterprise_score ? 'View' : 'Assess'}
                                  <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Medal className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                      <p className="text-muted-foreground">No Professional Sports organizations found</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="p-12 text-center">
              <Medal className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No Professional Sports Organizations</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                There are no Professional Sports organizations in the portfolio yet. Create a Professional Sports organization to start conducting enterprise risk assessments.
              </p>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Drill-down Prompt */}
      <Alert className="bg-blue-500/10 border-blue-500/30">
        <Eye className="w-4 h-4 text-blue-600" />
        <AlertDescription className="text-blue-600">
          <strong>Drill-Down Available:</strong> Switch to Organization View and select an organization to view their specific assessment history, take new assessments, and download policy templates.
        </AlertDescription>
      </Alert>
    </div>
  );
}
